import { __awaiter, __extends, __generator } from "tslib";
import { EventListener } from './event-listener';
import { externalAuthService, invalidCharactersMsg, invalidLengthMsg } from './external-auth-service';
import { LogLevel } from './interfaces';
import { AuthFrameMessageProcessor } from './MessageHandling/message-processor-auth';
import { FrameContexts } from './messages.interface';
import { isValidUrlForApp } from './utils';
import { WebContentSdkService } from './web-content-sdk';
// Time between pinging the auth window in ms
var pingTime = 100;
export var AuthFailedReason;
(function (AuthFailedReason) {
    AuthFailedReason["AppDefinitionUnavailable"] = "AppDefinitionUnavailable";
    AuthFailedReason["BlockedByBrowser"] = "BlockedByBrowser";
    AuthFailedReason["CancelledByUser"] = "CancelledByUser";
    AuthFailedReason["InvalidAuthUrl"] = "InvalidAuthUrl";
    AuthFailedReason["InvalidFrameContext"] = "InvalidFrameContext";
    AuthFailedReason["Other"] = "Other";
})(AuthFailedReason || (AuthFailedReason = {}));
var HostedWebContentAuthSdkService = /** @class */ (function (_super) {
    __extends(HostedWebContentAuthSdkService, _super);
    function HostedWebContentAuthSdkService(hostWindow, openWindowInfo, appDefinition, authenticationService, context, logger, telemetryService, isExternal) {
        var _this = this;
        var _a;
        _this = _super.call(this, hostWindow, context, authenticationService, logger, telemetryService, undefined) || this;
        _this.hostWindow = hostWindow;
        _this.openWindowInfo = openWindowInfo;
        _this.appDefinition = appDefinition;
        _this.authenticationService = authenticationService;
        _this.context = context;
        _this.logger = logger;
        _this.onAuthMessageReceived = function (event) { return __awaiter(_this, void 0, void 0, function () {
            var sourceOrigin;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.authWindow || this.authWindow !== event.source) {
                            return [2 /*return*/];
                        }
                        sourceOrigin = event.origin;
                        if (sourceOrigin !== this.origin && isValidUrlForApp(sourceOrigin, this.logger, this.appDefinition)) {
                            this.origin = event.origin;
                        }
                        if (!(this.origin === sourceOrigin && this.messageProcessor)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.messageProcessor.process(event)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        _this.setupAuthMessageHandlers = function () {
            if (_this.authenticationService) {
                _this.messageProcessor = new AuthFrameMessageProcessor(_this, _this.authenticationService, _this.telemetryService);
            }
        };
        if (_this.context.page.frameContext !== FrameContexts.authentication) {
            _this.HandleError('Invalid frame context for scenario', AuthFailedReason.InvalidFrameContext);
        }
        if (!_this.appDefinition) {
            _this.HandleError('App definition not available', AuthFailedReason.AppDefinitionUnavailable);
        }
        if (isValidUrlForApp(openWindowInfo.url, _this.logger, appDefinition) && _this.hostWindow) {
            var globalWindow = _this.hostWindow;
            var windowFeatures = '';
            if (openWindowInfo.width && openWindowInfo.height) {
                windowFeatures = "width=".concat(openWindowInfo.width, ",height=").concat(openWindowInfo.height);
                // compared with undefined because someone can pass 0 in these parameters
                if (openWindowInfo.left != undefined && openWindowInfo.top != undefined) {
                    windowFeatures = "".concat(windowFeatures, ",left=").concat(openWindowInfo.left, ",top=").concat(openWindowInfo.top);
                }
            }
            if (isExternal) {
                var doneHandler = function (success, result) {
                    if (success) {
                        authenticationService.notifySuccess(result);
                    }
                    else {
                        if (result === invalidLengthMsg || result === invalidCharactersMsg) {
                            logger.log(LogLevel.Error, 'HostedWebContentAuthSdkService', result);
                        }
                        authenticationService.notifyFailure(result);
                    }
                };
                _this.hostWindow.open(externalAuthService.startAuth(openWindowInfo.url, doneHandler, authenticationService.externalAuthRedirectUrl));
            }
            else {
                _this.authWindow = globalWindow.open(openWindowInfo.url, "appAuthWindow", windowFeatures);
                if (_this.authWindow) {
                    _this.origin = openWindowInfo.url;
                    _this.setUserClickTime((_a = context.app.userClickTime) !== null && _a !== void 0 ? _a : Date.now());
                    _this.setupAuthMessageHandlers();
                    _this.globalWindowEventListener = new EventListener(globalWindow);
                    _this.globalWindowEventListener.addListener('message', _this.onAuthMessageReceived);
                    _this.startAuthWindowMonitor();
                    _this.initializeAuthenticationContext(context);
                }
                else {
                    _this.HandleError('browser is blocking the url to open', AuthFailedReason.BlockedByBrowser);
                }
            }
        }
        else {
            _this.HandleError('Auth url is not valid', AuthFailedReason.InvalidAuthUrl);
        }
        return _this;
    }
    HostedWebContentAuthSdkService.prototype.getHostWindow = function () {
        return this.authWindow || null;
    };
    HostedWebContentAuthSdkService.prototype.postResponseToApp = function (id, args) {
        this.sendResponseMessage(id, args);
    };
    HostedWebContentAuthSdkService.prototype.isWindowOpen = function () {
        return this.authWindow ? !this.authWindow.closed : false;
    };
    /**
     * close auth window
     */
    HostedWebContentAuthSdkService.prototype.closeWindow = function () {
        this.unloadApp(this.appDefinition);
    };
    /**
     * to send response message to frame
     * @param id id used for callback function
     * @param args function arguments
     */
    HostedWebContentAuthSdkService.prototype.sendResponseMessage = function (id, args) {
        if (this.origin) {
            var response = {
                id: id,
                args: args,
                origin: this.origin,
            };
            this.postMessage(response);
        }
    };
    /**
     * to send request message to frame
     * @param functionName function name
     * @param funArguments function arguments
     */
    HostedWebContentAuthSdkService.prototype.sendRequestMessage = function (functionName, funArguments) {
        if (this.origin) {
            var request = {
                func: functionName,
                args: funArguments,
            };
            this.postMessage(request);
        }
    };
    HostedWebContentAuthSdkService.prototype.startAuthWindowMonitor = function () {
        var _this = this;
        this.stopAuthWindowMonitor();
        this.authWindowMonitor = this.hostWindow.setInterval(function () {
            _this.authWindowInterval();
        }, pingTime);
    };
    HostedWebContentAuthSdkService.prototype.authWindowInterval = function () {
        if (!this.isWindowOpen()) {
            this.HandleError('CancelledByUser', AuthFailedReason.CancelledByUser);
            this.closeWindow();
            this.stopAuthWindowMonitor();
        }
        else {
            // authentication window is pinged in order to restablish opener for SDK in case it is lost.
            this.sendRequestMessage('ping');
        }
    };
    HostedWebContentAuthSdkService.prototype.stopAuthWindowMonitor = function () {
        if (this.authWindowMonitor) {
            this.hostWindow.clearInterval(this.authWindowMonitor);
            this.authWindowMonitor = undefined;
        }
    };
    /**
     * function to execute when app frame is unloaded
     */
    HostedWebContentAuthSdkService.prototype.unloadApp = function (_appDefinition) {
        var _a;
        this.unloadSDK();
        (_a = this.authWindow) === null || _a === void 0 ? void 0 : _a.close();
    };
    /**
     * function to execute when app container is unmounted
     */
    HostedWebContentAuthSdkService.prototype.unloadSDK = function () {
        if (this.globalWindowEventListener) {
            this.globalWindowEventListener.removeListener('message');
        }
    };
    HostedWebContentAuthSdkService.prototype.postMessage = function (message) {
        var _a;
        if (this.origin) {
            (_a = this.authWindow) === null || _a === void 0 ? void 0 : _a.postMessage(message, this.origin);
        }
    };
    HostedWebContentAuthSdkService.prototype.HandleError = function (result, failureReason) {
        if (this.authenticationService) {
            this.authenticationService.notifyFailure(result, failureReason);
        }
    };
    return HostedWebContentAuthSdkService;
}(WebContentSdkService));
export { HostedWebContentAuthSdkService };
//# sourceMappingURL=hosted-web-content-auth-sdk.js.map